import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Settings, Eye } from 'lucide-react';
import { useDashboardStore } from '../store/dashboardStore';
import { useAuthStore } from '../store/authStore';

const DashboardsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const {
    getUserDashboards,
    getActiveDashboard,
    createDashboard,
    setActiveDashboard,
  } = useDashboardStore();

  const dashboards = user ? getUserDashboards(user.id) : [];
  const activeDashboard = user ? getActiveDashboard(user.id) : null;

  const handleCreateDashboard = () => {
    if (!user) return;

    const name = prompt('Enter dashboard name:');
    if (name?.trim()) {
      createDashboard(user.id, name.trim());
    }
  };

  const handleOpenDashboard = (dashboardId?: string) => {
    if (dashboardId && user) {
      setActiveDashboard(user.id, dashboardId);
    }
    navigate('/custom-dashboard');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 transition-all duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 dark:text-slate-200">
                Dashboards
              </h1>
              <p className="text-slate-600 dark:text-slate-400 mt-1">
                Create and manage custom dashboards with interactive widgets
              </p>
            </div>

            <button
              onClick={handleCreateDashboard}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-xl hover:from-purple-600 hover:to-purple-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 font-medium"
            >
              <Plus className="w-4 h-4" />
              New Dashboard
            </button>
          </div>
        </div>

        {/* Saved Dashboards */}
        {dashboards.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-bold text-slate-800 dark:text-slate-200 mb-4">
              Your Dashboards
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {dashboards.map((dashboard) => (
                <div
                  key={dashboard.id}
                  className="group bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm rounded-xl border border-slate-200/50 dark:border-slate-700/50 shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden"
                >
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold text-slate-800 dark:text-slate-200 truncate">
                        {dashboard.name}
                      </h3>
                      {activeDashboard?.id === dashboard.id && (
                        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                      )}
                    </div>

                    <div className="text-sm text-slate-600 dark:text-slate-400 mb-3">
                      {dashboard.widgets.length} widgets
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={() => handleOpenDashboard(dashboard.id)}
                        className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm font-medium"
                      >
                        <Eye className="w-4 h-4" />
                        Open
                      </button>
                      <button
                        onClick={() => {
                          setActiveDashboard(user!.id, dashboard.id);
                          navigate('/custom-dashboard');
                        }}
                        className="flex items-center justify-center px-3 py-2 bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors"
                      >
                        <Settings className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardsPage;
